import { useCallback, useEffect, useRef, useState } from 'react';
import { useThemeColors } from '@/shared/theme/useThemeColors';
import { semantic, shadow, radius } from '@/shared/theme/tokens';
import {
  identityTransform, zoomAtPoint, zoomByStep, clampTranslate, isZoomed, SCALE_STEP,
} from './zoomMath';

/**
 * Pan + zoom container. Wheel / pinch to zoom toward the cursor, drag to pan,
 * reset button to recenter. Children are rendered inside a CSS-transform layer.
 *
 * A drag only counts as a pan once the pointer moves past `dragThreshold` px; a
 * sub-threshold press is treated as a click and passes through to children, so
 * region selection inside the SVG keeps working.
 */
export function ZoomPanViewport({ children, dragThreshold = 4 }) {
  const c = useThemeColors();
  const wrapRef = useRef(null);
  const [t, setT] = useState(identityTransform);

  // Refs for in-flight gesture state (avoid re-render per pointermove).
  const drag = useRef(null);            // { startX, startY, baseX, baseY, moved }
  const pinch = useRef(null);           // { startDist, baseScale }
  const tRef = useRef(t);
  tRef.current = t;

  const size = useCallback(() => {
    const el = wrapRef.current;
    return el ? { w: el.clientWidth, h: el.clientHeight } : { w: 0, h: 0 };
  }, []);

  const reset = useCallback(() => setT(identityTransform()), []);

  // Wheel zoom toward cursor. Non-passive so we can preventDefault the page scroll.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const fx = e.clientX - rect.left;
      const fy = e.clientY - rect.top;
      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      setT((cur) => clampTranslate(zoomAtPoint(cur, factor, fx, fy), rect.width, rect.height));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  function pointerDist(touches) {
    const [a, b] = touches;
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  }

  function onPointerDown(e) {
    if (e.pointerType === 'touch') return; // touch handled via touch events below
    drag.current = { startX: e.clientX, startY: e.clientY, baseX: tRef.current.x, baseY: tRef.current.y, moved: false };
  }
  function onPointerMove(e) {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) < dragThreshold) return;
    d.moved = true;
    const { w, h } = size();
    setT((cur) => clampTranslate({ scale: cur.scale, x: d.baseX + dx, y: d.baseY + dy }, w, h));
  }
  function endDrag(e) {
    const d = drag.current;
    drag.current = null;
    // If the pointer moved (a pan), swallow the trailing click so it doesn't
    // select a region underneath.
    if (d?.moved && e) {
      const stop = (ev) => { ev.stopPropagation(); ev.preventDefault(); window.removeEventListener('click', stop, true); };
      window.addEventListener('click', stop, true);
      setTimeout(() => window.removeEventListener('click', stop, true), 0);
    }
  }

  // Touch: 1-finger pan, 2-finger pinch.
  function onTouchStart(e) {
    if (e.touches.length === 2) {
      pinch.current = { startDist: pointerDist(e.touches), baseScale: tRef.current.scale };
    } else if (e.touches.length === 1) {
      const tch = e.touches[0];
      drag.current = { startX: tch.clientX, startY: tch.clientY, baseX: tRef.current.x, baseY: tRef.current.y, moved: false };
    }
  }
  function onTouchMove(e) {
    const el = wrapRef.current;
    if (pinch.current && e.touches.length === 2) {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const dist = pointerDist(e.touches);
      const factor = dist / pinch.current.startDist;
      const target = pinch.current.baseScale * factor;
      const fx = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
      const fy = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
      setT((cur) => clampTranslate(zoomAtPoint({ ...cur, scale: pinch.current.baseScale }, target / pinch.current.baseScale, fx, fy), rect.width, rect.height));
    } else if (drag.current && e.touches.length === 1) {
      const tch = e.touches[0];
      const dx = tch.clientX - drag.current.startX;
      const dy = tch.clientY - drag.current.startY;
      if (!drag.current.moved && Math.hypot(dx, dy) < dragThreshold) return;
      drag.current.moved = true;
      e.preventDefault();
      const { w, h } = size();
      setT((cur) => clampTranslate({ scale: cur.scale, x: drag.current.baseX + dx, y: drag.current.baseY + dy }, w, h));
    }
  }
  function onTouchEnd(e) {
    if (e.touches.length === 0) { drag.current = null; pinch.current = null; }
  }

  const zoomed = isZoomed(t);

  function stepZoom(delta) {
    const { w, h } = size();
    setT((cur) => clampTranslate(zoomByStep(cur, delta, w, h), w, h));
  }

  return (
    <div
      ref={wrapRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerLeave={endDrag}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
        position: 'relative', width: '100%', height: '100%', overflow: 'hidden',
        touchAction: 'none', cursor: drag.current?.moved ? 'grabbing' : (zoomed ? 'grab' : 'default'),
      }}
    >
      <div style={{
        width: '100%', height: '100%',
        transform: `translate(${t.x}px, ${t.y}px) scale(${t.scale})`,
        transformOrigin: '0 0',
        transition: drag.current ? 'none' : 'transform 0.12s ease-out',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {children}
      </div>

      {/* Zoom controls */}
      <div style={{
        position: 'absolute', right: 8, bottom: 8, zIndex: 10,
        display: 'flex', flexDirection: 'column', gap: 4,
        background: c.bg, border: `1px solid ${c.border}`, borderRadius: radius.lg, padding: 4,
        boxShadow: shadow.card,
      }}>
        <ZoomBtn icon="add" title="Zoom in" onClick={() => stepZoom(SCALE_STEP)} c={c} />
        <ZoomBtn icon="remove" title="Zoom out" onClick={() => stepZoom(-SCALE_STEP)} c={c} />
        <ZoomBtn icon="restart_alt" title="Reset" onClick={reset} c={c} disabled={!zoomed} />
      </div>
    </div>
  );
}

function ZoomBtn({ icon, title, onClick, c, disabled }) {
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 28, height: 28, borderRadius: radius.md, border: `1px solid ${c.borderSubtle}`,
        background: disabled ? c.bgSubtle : c.bg, cursor: disabled ? 'default' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
        color: disabled ? c.textMuted : semantic.brandNavyMid,
      }}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 17 }}>{icon}</span>
    </button>
  );
}
